import React, { useEffect, useRef } from "react";
import { Authenticator } from "@aws-amplify/ui-react";
import { LuX, LuLock } from "react-icons/lu";
import markLogo from "../../assets/images/no-text-logo.svg";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function LoginDialog({ open, onClose }) {
  const panelRef = useRef(null);
  // Read through a ref so a new onClose identity doesn't re-run the effect
  // (which would re-steal focus mid-typing).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }

      // Keep Tab focus inside the dialog
      if (e.key === "Tab" && panelRef.current) {
        const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter(
          (el) => el.offsetParent !== null
        );
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    // Focus the first field once Amplify has rendered the form
    const focusTimer = setTimeout(() => {
      const first = panelRef.current?.querySelector("input");
      (first || panelRef.current)?.focus();
    }, 50);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="login-dialog" onMouseDown={onClose}>
      <div
        className="login-dialog__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-dialog-title"
        tabIndex={-1}
        ref={panelRef}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="login-dialog__close"
          onClick={onClose}
          aria-label="Close sign in"
        >
          <LuX />
        </button>

        <div className="login-dialog__head">
          <img src={markLogo} alt="" className="login-dialog__mark" />
          <h2 id="login-dialog-title">Welcome back</h2>
          <p>Sign in to your Care Connect account</p>
        </div>

        <Authenticator hideSignUp />

        <p className="login-dialog__foot">
          <LuLock /> Secure sign-in. Accounts are created by your administrator.
        </p>
      </div>
    </div>
  );
}
